import React, { useState, useEffect } from 'react';
import { Search, Download, FileText, Eye, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../components/ui/utils';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { Asset, AssetStatus, AssetCondition } from '../../../types/asset';
import { toSydneyDate } from '../../../utils/helpers';
import { Combobox } from '../../../components/ui/combobox';
import { User } from '../../../types';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../../constants/permissions.constants";

const AssetRegisterReport: React.FC = () => {
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const [assets, setAssets] = useState<Asset[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('All Types');
    const [statusFilter, setStatusFilter] = useState('All Statuses');
    const [assignedToFilter, setAssignedToFilter] = useState<string | undefined>(undefined);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [assetsRes, usersRes] = await Promise.all([
                    api.get<Asset[]>(`/api/assets`),
                    api.get<{ users: User[] }>('/api/users?all_users=true')
                ]);
                setAssets(assetsRes);
                setUsers(Array.isArray(usersRes) ? usersRes : usersRes.users || []);
            } catch (error) {
                console.error('Error fetching assets for report:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const filteredAssets = assets.filter(asset => {
        const matchesSearch = asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            asset.tag.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (asset.brand && asset.brand.toLowerCase().includes(searchQuery.toLowerCase()));

        const matchesType = typeFilter === 'All Types' || asset.type === typeFilter;
        const matchesStatus = statusFilter === 'All Statuses' || asset.status === statusFilter;
        
        const matchesAssignedTo = !assignedToFilter || 
            (asset.assignedToUser && asset.assignedToUser.id.toString() === assignedToFilter);

        return matchesSearch && matchesType && matchesStatus && matchesAssignedTo;
    });

    const totalAssets = filteredAssets.length;
    const totalValue = filteredAssets.reduce((acc, curr) => acc + (Number(curr.purchasePrice) || 0), 0);
    const assignedCount = filteredAssets.filter(a => a.assignedToUser || a.assignedToDepartment).length;
    const availableCount = filteredAssets.filter(a => a.status === 'Available').length;

    const downloadCSV = () => {
        const canReadValue = ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE);
        const headers = ['Asset ID', 'Asset Name', 'Type', 'Brand/Model', 'Serial Number', 'Status', 'Condition', 'Location', 'Assignment', 'Purchase Date', ...(canReadValue ? ['Purchase Price'] : []), 'Warranty Expiry'];
        const rows = filteredAssets.map(asset => [
            asset.tag,
            asset.name,
            asset.type,
            `${asset.brand || ''} ${asset.model || ''}`.trim(),
            asset.serialNumber || '',
            asset.status,
            asset.condition || '',
            asset.location || '',
            asset.assignedToUser?.name || asset.assignedToDepartment?.departmentName || 'Unassigned',
            toSydneyDate(asset.purchaseDate),
            ...(canReadValue ? [asset.purchasePrice ? Number(asset.purchasePrice).toFixed(2) : ''] : []),
            toSydneyDate(asset.warrantyExpiryDate)
        ]);

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `asset-register-report-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const getStatusStyles = (status: AssetStatus) => {
        switch (status) {
            case 'Assigned': return 'bg-blue-50 text-blue-600 border-blue-100';
            case 'Available': return 'bg-green-50 text-green-600 border-green-100';
            case 'In Maintenance': return 'bg-amber-50 text-amber-600 border-amber-100';
            case 'Needs Repair':
            case 'Damaged': return 'bg-red-50 text-red-600 border-red-100';
            case 'Lost': return 'bg-gray-800 text-white border-gray-900';
            case 'Archived':
            case 'Retired':
            case 'Disposed': return 'bg-gray-100 text-gray-500 border-gray-200';
            default: return 'bg-gray-50 text-gray-600 border-gray-100';
        }
    };

    const getConditionStyles = (condition?: AssetCondition | string) => {
        if (!condition) return 'bg-gray-100 text-gray-500';
        switch (condition) {
            case 'New': return 'bg-emerald-100 text-emerald-700';
            case 'Excellent': return 'bg-green-100 text-green-700';
            case 'Good': return 'bg-blue-100 text-blue-700';
            case 'Fair': return 'bg-yellow-100 text-yellow-700';
            case 'Poor': return 'bg-orange-100 text-orange-700';
            case 'Damaged': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-500';
        }
    };

    return (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden animate-in fade-in duration-500">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-50 rounded-lg">
                            <FileText size={20} className="text-gray-600" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg">Asset Register Report</h3>
                            <p className="text-gray-500 text-sm font-medium">Complete inventory of all assets - Single source of truth</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2" onClick={downloadCSV}>
                            <Download size={16} />
                            <span>CSV</span>
                        </Button>
                    </div>
                </div>

                {/* Filters Row */}
                <div className="mt-6 flex flex-col md:flex-row items-stretch md:items-center gap-4">
                    <div className="relative w-full flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <Input
                            placeholder="Search assets..."
                            className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium w-full"
                            value={searchQuery}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                        <div className="w-full md:w-48">
                            <Combobox
                                options={users.map(u => ({ label: u.name, value: u.id.toString() }))}
                                value={assignedToFilter}
                                onChange={setAssignedToFilter}
                                placeholder="Assigned To"
                                searchPlaceholder="Search person..."
                                emptyMessage="No person found."
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 w-full md:w-auto">
                            <Select value={typeFilter} onValueChange={setTypeFilter}>
                                <SelectTrigger className="h-11 px-4 rounded-xl border-gray-100 bg-gray-50/50 text-sm font-bold text-gray-700 w-full md:w-40">
                                    <SelectValue placeholder="All Types" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl border-gray-100 shadow-xl">
                                    <SelectItem value="All Types" className="font-bold">All Types</SelectItem>
                                    <SelectItem value="Vehicle">Vehicle</SelectItem>
                                    <SelectItem value="Plant & Equipment">Plant & Equipment</SelectItem>
                                    <SelectItem value="Tools">Tools</SelectItem>
                                    <SelectItem value="IT Equipment">IT Equipment</SelectItem>
                                    <SelectItem value="Mobile Devices">Mobile Devices</SelectItem>
                                    <SelectItem value="Lifting/Slings">Lifting/Slings</SelectItem>
                                    <SelectItem value="Furniture">Furniture</SelectItem>
                                </SelectContent>
                            </Select>

                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="h-11 px-4 rounded-xl border-gray-100 bg-gray-50/50 text-sm font-bold text-gray-700 w-full md:w-40">
                                    <SelectValue placeholder="All Statuses" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl border-gray-100 shadow-xl">
                                    <SelectItem value="All Statuses" className="font-bold">All Statuses</SelectItem>
                                    <SelectItem value="Assigned">Assigned</SelectItem>
                                    <SelectItem value="Available">Available</SelectItem>
                                    <SelectItem value="In Maintenance">In Maintenance</SelectItem>
                                    <SelectItem value="Needs Repair">Needs Repair</SelectItem>
                                    <SelectItem value="Damaged">Damaged</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                {/* Stats Row */}
                <div className="mt-6 bg-gray-50 rounded-2xl p-6 flex flex-wrap items-center justify-between gap-6">
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Assets</p>
                        <h3 className="text-2xl font-black text-gray-900">{totalAssets}</h3>
                    </div>
                    {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                        <div className="flex-1 min-w-[120px]">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Value</p>
                            <h3 className="text-2xl font-black text-gray-900 font-mono">
                                ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </h3>
                        </div>
                    )}
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Assigned</p>
                        <h3 className="text-2xl font-black text-gray-900">{assignedCount}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Available</p>
                        <h3 className="text-2xl font-black text-gray-900">{availableCount}</h3>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto no-scrollbar pb-4">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-white border-b border-gray-100">
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Asset ID</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Asset Name</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Brand/Model</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Serial Number</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Condition</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Location</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Assignment</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Purchase Date</th>
                            {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                                <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Purchase Price</th>
                            )}
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Warranty</th>
                            <th className="px-6 py-4 text-right text-[11px] font-bold text-gray-800 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? (
                            [...Array(3)].map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    <td colSpan={13} className="px-6 py-4"><div className="h-10 bg-gray-50 rounded-xl w-full" /></td>
                                </tr>
                            ))
                        ) : filteredAssets.length === 0 ? (
                            <tr>
                                <td colSpan={13} className="px-6 py-12 text-center text-gray-400">
                                    No assets found matching the criteria.
                                </td>
                            </tr>
                        ) : (
                            filteredAssets.map((asset) => (
                                <tr key={asset.id} className="hover:bg-gray-50/50 transition-colors group">
                                    <td className="px-6 py-4">
                                        <span className="text-xs font-bold text-gray-500 font-mono tracking-wider">{asset.tag}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="min-w-[180px]">
                                            <p className="font-bold text-gray-900 text-[13px]">{asset.name}</p>
                                            {asset.description && (
                                                <p className="text-[11px] font-medium text-gray-400 truncate mt-0.5 max-w-[200px]">{asset.description}</p>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="text-[11px] font-bold text-gray-600 border border-gray-200 rounded-md px-2.5 py-1 whitespace-nowrap bg-white">{asset.type}</span>
                                    </td>
                                    <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                        {asset.brand} {asset.model}
                                    </td>
                                    <td className="px-6 py-4 text-[13px] text-gray-500 font-mono">
                                        {asset.serialNumber || '-'}
                                    </td>
                                    <td className="px-6 py-4">
                                        <Badge variant="outline" className={cn("rounded-lg border-0 font-bold px-2 py-1 text-[11px] whitespace-nowrap", getStatusStyles(asset.status))}>
                                            {asset.status === 'Needs Repair' ? (
                                                <span className="flex items-center gap-1.5">
                                                    <AlertTriangle size={12} /> Needs Repair
                                                </span>
                                            ) : asset.status === 'Available' ? (
                                                <span className="flex items-center gap-1.5">
                                                    <CheckCircle2 size={12} /> Available
                                                </span>
                                            ) : (
                                                <span>{asset.status}</span>
                                            )}
                                        </Badge>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Badge variant="outline" className={cn("rounded-md border-0 font-bold px-2.5 py-0.5 text-[11px]", getConditionStyles(asset.condition))}>
                                            {asset.condition || '-'}
                                        </Badge>
                                    </td>
                                    <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                        {asset.location || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                        {asset.assignedToUser?.name || asset.assignedToDepartment?.departmentName || 'Unassigned'}
                                    </td>
                                    <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                        {toSydneyDate(asset.purchaseDate) || '-'}
                                    </td>
                                    {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                                        <td className="px-6 py-4 text-[13px] font-medium text-gray-700 font-mono">
                                            {asset.purchasePrice ? `$${Number(asset.purchasePrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                        </td>
                                    )}
                                    <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap text-center">
                                        {toSydneyDate(asset.warrantyExpiryDate) || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="w-8 h-8 rounded-lg text-gray-400 hover:text-gray-900 transition-colors"
                                            onClick={() => navigate(`/assets/${asset.id}`)}
                                        >
                                            <Eye size={16} />
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AssetRegisterReport;
